import { jsxs, jsx } from "react/jsx-runtime";
import assert from "assert";
import { useContext } from "react";
import { useFallbackId } from "../../hooks/useFallbackId.mjs";
import { Box } from "../Box/Box.mjs";
import { HeadingContext } from "../Heading/HeadingContext.mjs";
import { TextContext } from "../Text/TextContext.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { validateTabIndex } from "../private/validateTabIndex.mjs";
import { select, focusRing } from "./TextDropdown.css.mjs";
import { IconChevron } from "../icons/IconChevron/IconChevron.mjs";
function parseSimpleToComplexOption(option) {
  return typeof option === "object" && option !== null && "text" in option && "value" in option ? option : { value: option, text: String(option) };
}
function TextDropdown({
  id,
  value,
  onChange,
  onBlur,
  options,
  label,
  data,
  tabIndex,
  ...restProps
}) {
  assert(
    (() => {
      const inText = useContext(TextContext);
      const inHeading = useContext(HeadingContext);
      return inText || inHeading;
    })(),
    "TextDropdown components must be rendered within a Text or Heading component. See the documentation for correct usage: https://seek-oss.github.io/braid-design-system/components/TextDropdown"
  );
  if (process.env.NODE_ENV !== "production") {
    validateTabIndex(tabIndex);
  }
  const parsedOptions = options.map(parseSimpleToComplexOption);
  const [currentText] = parsedOptions.filter((o) => value === o.value);
  if (!currentText || !currentText.text) {
    throw new Error(
      `The provided value of "${value}" does not exist in the provided \`options\` list.`
    );
  }
  const resolvedId = useFallbackId(id);
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "inlineBlock",
      position: "relative",
      ...buildDataAttributes({ data, validateRestProps: restProps }),
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            component: "select",
            position: "absolute",
            inset: 0,
            width: "full",
            opacity: 0,
            className: select,
            "aria-label": label,
            title: label,
            id: resolvedId,
            tabIndex,
            value: String(value),
            onChange: (ev) => {
              if (typeof onChange === "function") {
                const newValue = parsedOptions[ev.currentTarget.selectedIndex].value;
                onChange(newValue);
              }
            },
            onBlur,
            children: /* @__PURE__ */ jsx("optgroup", { label, children: parsedOptions.map((option) => /* @__PURE__ */ jsx("option", { value: String(option.value), children: option.text }, String(option.value))) })
          }
        ),
        /* @__PURE__ */ jsxs(Box, { pointerEvents: "none", userSelect: "none", className: focusRing, children: [
          currentText.text,
          " ",
          /* @__PURE__ */ jsx(IconChevron, { alignY: "lowercase" })
        ] })
      ]
    }
  );
}
export {
  TextDropdown,
  parseSimpleToComplexOption
};
